/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IAriSearchResultsData } from './ari-search-results.data';
import { IAriSearchResultsProps } from './ari-search-results.props.autogenerated';
import { IAriModelEntity, IAriServiceModelExtension } from '../../actions/DataServiceEntities.g';
import { IAriModel } from './interfaces/IAriModel';
import { AriSearchResultTableComponent, IAriSearchResultTableResources } from './components/AriSearchResultTable.component';
import { QueryResultSettings } from '@msdyn365-commerce/retail-proxy';
import { QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import searchModelsAction, { SearchModelsInput } from '../../actions/search-models.action';
import Pagination from '../../components/Pagination';
import generateAriModelDetailUrl from '../../utils/getAriModelDetailUrl';
/**
 * Props for the view component
 */
export interface IAriSearchResultsViewProps extends IAriSearchResultsProps<IAriSearchResultsData> {
    modelTable: React.ReactNode;
    pagination: React.ReactNode;
}

/**
 * Component state
 */
interface IAriSearchResultsState {
    page: number;
    models: IAriServiceModelExtension[];
    totalCount: number;
}

/**
 *
 * AriSearchResults component
 * @extends {React.PureComponent<IAriSearchResultsProps<IAriSearchResultsData>>}
 */
class AriSearchResults extends React.PureComponent<IAriSearchResultsProps<IAriSearchResultsData>, IAriSearchResultsState> {
    constructor(props: IAriSearchResultsProps<IAriSearchResultsData>) {
        super(props);
        const queryResultSettings = QueryResultSettingsProxy.fromInputData(props.context.actionContext).QueryResultSettings;
        let page = 1;
        if (queryResultSettings.Paging?.Skip && queryResultSettings.Paging.Skip > 0) {
            page = Math.floor(queryResultSettings.Paging.Skip / (this.props.config.itemsPerPage || 10)) + 1;
        }
        this.state = {
            page,
            models: this.props.data.ariResults.result?.ariSearchResults.Models || [],
            totalCount: this.props.data.ariResults.result?.ariSearchResults.TotalCount || 0
        };
        this.handlePaginationPageChange = this.handlePaginationPageChange.bind(this);

        this.updateModelResults();
    }

    /**
     * Generates the id for the product detail page
     * @param modelId model id for the page url
     * @param brandCode brand code for the page url
     * @private
     */
    private getModelDetailPageUrl(modelId: string, brandCode: string) {
        return generateAriModelDetailUrl(modelId, brandCode, this.props.context.actionContext);
    }

    /**
     * Updates the results of the model when paginating
     * @private
     */
    private updateModelResults() {
        // Explicitly setting isRefineEnabled to true as this functionality no longer needed.
        // const isRefineEnabled: boolean = this.props.context.request.query?.refine ? true : false;

        const isRefineEnabled = this.props.config.expandResults || false;
        const enableDirectLinkAndRedirect = this.props.config.enableDirectLinkAndRedirect;

        const top = this.props.config.itemsPerPage || 10;
        const queryResultSettings: QueryResultSettings = {
            Paging: {
                Top: top,
                Skip: (this.state.page - 1) * top
            }
        };
        let searchTerm = '';

        if (enableDirectLinkAndRedirect) {
            const searchTerms = this.getSearchTermsFromQuery();
            // Limit to 5 parameters
            const limitedSearchTerms = searchTerms.slice(0, 5);
            searchTerm = limitedSearchTerms.join(';');
        } else {
            searchTerm = this.props.context.request.query?.q || '';
        }

        const {
            context: {
                request: { apiSettings },
                request,
                actionContext
            }
        } = this.props;
        const pageType =
            this.props.context.request.urlTokens.pageType === 'Category' || (request.query && request.query.categoryId)
                ? 'Category'
                : 'Search';
        const searchModelInput = new SearchModelsInput(
            searchTerm,
            queryResultSettings,
            pageType,
            apiSettings,
            isRefineEnabled,
            enableDirectLinkAndRedirect
        );
        void searchModelsAction(searchModelInput, actionContext).then(result => {
            const models = result.ariSearchResults.Models || [];
            const totalCount = result.ariSearchResults.TotalCount;

            // Auto-redirect if exactly one model
            if (enableDirectLinkAndRedirect && totalCount === 1) {
                const model = models[0];
                const detailUrl = this.getModelDetailPageUrl(model.Id.toString(), model.BrandCode!);
                window.location.href = detailUrl;
                return;
            }
            this.setState({
                models,
                totalCount
            });
        });
    }

    private getSearchTermsFromQuery(): string[] {
        const queryParams = this.props.context.request.query || {};
        const searchTerms: string[] = [];

        Object.keys(queryParams).forEach(key => {
            if (key.startsWith('q') && queryParams[key]) {
                searchTerms.push(queryParams[key] as string);
            }
        });

        return searchTerms;
    }

    /**
     * Renders the model table
     * @param modelsList model list for the table
     * @param totalCount total count of models for pagination
     * @private
     */
    private getModelTable(modelsList: IAriModelEntity[], totalCount: number) {
        if (!modelsList?.length) {
            return null;
        }
        const modelsListWithPageLinks: IAriModel[] = modelsList.map(model => {
            return {
                ...model,
                detailPageUrl: this.getModelDetailPageUrl(model.Id.toString(), model.BrandCode!) || ''
            };
        });

        const { modelHeading, manufacturerHeading, tableHeading, descriptionHeading } = this.props.resources;
        const ariSearchResultTableResources: IAriSearchResultTableResources = {
            modelHeading,
            manufacturerHeading,
            tableHeading,
            descriptionHeading
        };
        return (
            <AriSearchResultTableComponent
                searchResults={modelsListWithPageLinks}
                resources={ariSearchResultTableResources}
                totalCount={totalCount}
            />
        );
    }

    /**
     * Handles user pagination
     * @param pageNumber page to change to
     * @private
     */
    private handlePaginationPageChange(pageNumber: number) {
        this.setState(
            {
                page: pageNumber
            },
            () => this.updateModelResults()
        );
    }

    private generateUrlForPagination() {
        const url = this.props.context.request.url;
        return url.requestUrl.toString();
    }

    /**
     * renders the pagination
     * @private
     */
    private renderPagination() {
        const {
            config: { itemsPerPage },
            resources: { previousText, nextText, paginationAriaLabel }
        } = this.props;
        const totalCount = this.state.totalCount;

        return (
            <>
                {totalCount > 0 && (
                    <Pagination
                        className='ms-search-result-container__pagination'
                        role='navigation'
                        aria-label={paginationAriaLabel}
                        qsp='skip'
                        items={totalCount}
                        itemsPerPage={itemsPerPage || 10}
                        startingItem={this.state.page}
                        previousText={this._renderPrev(previousText)}
                        nextText={this._renderNext(nextText)}
                        previousAriaLabel={previousText}
                        nextAriaLabel={nextText}
                        url={this.generateUrlForPagination()}
                        onPageChange={this.handlePaginationPageChange}
                        page={this.state.page}
                    />
                )}
            </>
        );
    }

    // private renderExpandOrRefineResults = () => {
    //     if (MsDyn365.isBrowser && typeof window !== 'undefined') {
    //         const url = new URL(window.location.href);
    //         const query = url.searchParams.get('q');

    //         if (!query) {
    //             return null;
    //         }

    //         const trimmedQuery = query.trim();
    //         const hasMultipleTerms = /\s|-/g.test(trimmedQuery);

    //         if (!hasMultipleTerms) {
    //             return null;
    //         }

    //         const isRefine = url.searchParams.has('refine');

    //         let link;
    //         if (!isRefine) {
    //             url.searchParams.set('refine', 'true');
    //             link = <a href={url.toString()}>{this.props.resources.expandResultsText}</a>;
    //         } else {
    //             url.searchParams.delete('refine');
    //             link = <a href={url.toString()}>{this.props.resources.refineResultsText}</a>;
    //         }
    //         return (
    //             <span className='ms-search-result-container__not-finding'>
    //                 {this.props.resources.notFindingText}&nbsp;{link}
    //             </span>
    //         );
    //     }
    //     return null;
    // };

    /**
     * Renders a Pagination previous text.
     * @param  paginationPreviousText - Pagination previous text from resources.
     * @returns Previous text and flipper icon.
     */
    private readonly _renderPrev = (paginationPreviousText: string) => {
        return (
            <div className='msc-pagination__prev'>
                <span className='ms-search-result__pagination-left' aria-hidden='true' />
                <span className='prev-text'>{paginationPreviousText}</span>
            </div>
        );
    };

    /**
     * Renders a Pagination next text.
     * @param  paginationNextText - Pagination next text from resources.
     * @returns Next text and flipper icon.
     */
    private readonly _renderNext = (paginationNextText: string) => {
        return (
            <div className='msc-pagination__next'>
                <span className='next-text'>{paginationNextText}</span>
                <span className='ms-search-result__pagination-right' aria-hidden='true' />
            </div>
        );
    };

    public render(): JSX.Element | null {
        const { models, totalCount } = this.state;

        const viewProps: IAriSearchResultsViewProps = {
            ...this.props,
            modelTable: this.getModelTable(models, totalCount),
            pagination: this.renderPagination()
            // notFindingMessage: this.renderExpandOrRefineResults()
        };
        return this.props.renderView(viewProps);
    }
}

export default AriSearchResults;
